/**
 * Cash flow: the position, the obligations, and the forecast (KAN-35).
 *
 * Three tickets meet in this file. KAN-48 asks for the inputs, KAN-89 for the
 * recurring obligations that make up most of the outflow, and KAN-90 for the
 * rolling 30/60/90-day forecast over both. The calendar arithmetic lives here,
 * in shared code, rather than in the API — a pay date is the kind of thing a
 * screen wants to show before it is saved, and two implementations of "the 31st
 * in February" would eventually disagree in front of Finance.
 *
 * **Nothing in this module, or anything that consumes it, initiates a payment.**
 * That is KAN-89's and KAN-90's shared constraint and it is structural: there is
 * no representation of a payment instruction here, no account number, and no
 * verb other than "project" and "rank". Money moves at the bank, by a person.
 */
/* ------------------------------------------------------------------------ *
 * Recurring obligations (KAN-89)
 * ------------------------------------------------------------------------ */
/**
 * What kind of outflow this is.
 *
 * Named after what Finance calls them rather than after an accounting
 * classification, because the person maintaining this list is the person who
 * writes the cheques. `payroll` and `payroll_tax` are separate because they run
 * on different schedules — a deposit is due on a rhythm set by the IRS, not by
 * the firm's pay cycle — and collapsing them is how a forecast misses the
 * largest deterministic outflow a firm has.
 */
export const OBLIGATION_KINDS = [
    'payroll',
    'payroll_tax',
    'income_tax',
    'rent',
    'insurance',
    'subscription',
    'loan',
    'utility',
    'other',
];
export const OBLIGATION_KIND_LABELS = {
    payroll: 'Payroll',
    payroll_tax: 'Payroll taxes',
    income_tax: 'Estimated income tax',
    rent: 'Rent',
    insurance: 'Insurance',
    subscription: 'Subscriptions',
    loan: 'Loan or lease',
    utility: 'Utilities',
    other: 'Other',
};
/**
 * How often it recurs.
 *
 * `biweekly` and `semi_monthly` are both present and are genuinely different:
 * biweekly is every fourteen days and drifts against the calendar, producing
 * twenty-six payments a year and two three-payment months; semi-monthly is
 * twice a calendar month and produces twenty-four. Firms use both, often for
 * different populations, and a forecast that treated them as the same thing
 * would be short by a full payroll twice a year.
 *
 * `one_off` is here rather than in a separate table because KAN-89 asks for
 * one-off expected outflows and they are the same row with one occurrence — a
 * second table would mean a second place the forecast has to remember to read.
 */
export const OBLIGATION_FREQUENCIES = [
    'weekly',
    'biweekly',
    'semi_monthly',
    'monthly',
    'quarterly',
    'annual',
    'one_off',
];
export const OBLIGATION_FREQUENCY_LABELS = {
    weekly: 'Weekly',
    biweekly: 'Every two weeks',
    semi_monthly: 'Twice monthly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annual: 'Annual',
    one_off: 'One-off',
};
/**
 * How certain the amount is.
 *
 * KAN-89 asks for these to be visually distinguished, and the reason is worth
 * stating: a forecast that renders contracted rent and an estimated utility
 * bill identically implies a precision it does not have, and the first time an
 * estimate is wrong the whole forecast loses credibility rather than the one
 * line that deserved to.
 */
export const OBLIGATION_CONFIDENCES = ['known', 'estimated'];
/**
 * What to do when a scheduled date lands on a weekend or a federal holiday.
 *
 * There is no default that is right for every obligation, which is why this is
 * a field rather than a constant. Payroll is almost always moved *earlier* —
 * paying staff late is not an option — while a vendor direct debit typically
 * falls on the next business day, and a date somebody entered as a deadline
 * should not move at all. Finance sets it per obligation, and the forecast
 * prints which rule produced each date.
 */
export const DATE_ADJUSTMENTS = ['none', 'previous_business_day', 'next_business_day'];
export const DATE_ADJUSTMENT_LABELS = {
    none: 'Keep the date',
    previous_business_day: 'Move earlier to the previous business day',
    next_business_day: 'Move later to the next business day',
};
function toParts(date) {
    const parts = date.split('-');
    return {
        year: Number.parseInt(parts[0] ?? '0', 10),
        month: Number.parseInt(parts[1] ?? '1', 10),
        day: Number.parseInt(parts[2] ?? '1', 10),
    };
}
function fromParts(year, month, day) {
    const pad = (value) => String(value).padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}`;
}
/** Days in a month, Gregorian, February included. */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * A date built from a month and a *nominal* day of month, clamped to the end of
 * short months.
 *
 * This is KAN-89's first named edge case. An obligation due "on the 31st" is due
 * on the 28th of February, not skipped and not rolled into March — the firm pays
 * its rent in February. Clamping rather than rolling is also what keeps twelve
 * monthly occurrences in a twelve-month window: rolling would produce eleven in
 * some years and thirteen in others.
 */
export function dayOfMonthClamped(year, month, nominalDay) {
    return fromParts(year, month, Math.min(nominalDay, daysInMonth(year, month)));
}
/**
 * Whole calendar days from one date to another, inclusive of neither end.
 *
 * Counted in UTC on `YYYY-MM-DD`, which is the only way to get an integer: a
 * year spanning two daylight-saving transitions is not a whole number of
 * 24-hour days, and a `DateTime.diff` across one comes back at 364.958. Two
 * separate features have now been wrong in exactly that way — an annual budget
 * losing $7,500 to nothing, and a mid-month joiner given a day of capacity they
 * were not employed for — so there is one implementation and both use it.
 */
export function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
/** Day of week, 0 = Sunday. Computed in UTC so no timezone can shift it. */
export function dayOfWeek(date) {
    const { year, month, day } = toParts(date);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
export function addDays(date, days) {
    const { year, month, day } = toParts(date);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return fromParts(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}
export function addMonths(date, months) {
    const { year, month, day } = toParts(date);
    const total = (year * 12 + (month - 1)) + months;
    const targetYear = Math.floor(total / 12);
    const targetMonth = (total % 12) + 1;
    return dayOfMonthClamped(targetYear, targetMonth, day);
}
/**
 * The nth given weekday of a month, e.g. the third Monday of January.
 *
 * Used only by the holiday table below. Kept general because five of the eleven
 * US federal holidays are defined this way and expressing each one separately
 * would be five chances to get the arithmetic wrong.
 */
function nthWeekdayOfMonth(year, month, weekday, n) {
    const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    const offset = (weekday - firstWeekday + 7) % 7;
    return fromParts(year, month, 1 + offset + (n - 1) * 7);
}
function lastWeekdayOfMonth(year, month, weekday) {
    const last = daysInMonth(year, month);
    const lastWeekday = new Date(Date.UTC(year, month - 1, last)).getUTCDay();
    const offset = (lastWeekday - weekday + 7) % 7;
    return fromParts(year, month, last - offset);
}
/**
 * US federal holidays for a year, as the dates they are *observed*.
 *
 * Federal rather than a firm-specific list, and that is a stated assumption
 * rather than a discovery: banks close on federal holidays, and it is the bank
 * being shut that moves a payment, not the office being shut. A firm that also
 * closes on, say, the day after Thanksgiving still clears payments that day.
 *
 * The Saturday/Sunday observation rule is the federal one — a holiday falling
 * on a Saturday is observed the Friday before, and one falling on a Sunday the
 * Monday after — because that is when the banks are actually closed.
 */
export function federalHolidays(year) {
    const fixed = [
        fromParts(year, 1, 1), // New Year's Day
        fromParts(year, 6, 19), // Juneteenth
        fromParts(year, 7, 4), // Independence Day
        fromParts(year, 11, 11), // Veterans Day
        fromParts(year, 12, 25), // Christmas Day
    ];
    const floating = [
        nthWeekdayOfMonth(year, 1, 1, 3), // Martin Luther King Jr. Day
        nthWeekdayOfMonth(year, 2, 1, 3), // Washington's Birthday
        lastWeekdayOfMonth(year, 5, 1), // Memorial Day
        nthWeekdayOfMonth(year, 9, 1, 1), // Labor Day
        nthWeekdayOfMonth(year, 10, 1, 2), // Columbus Day
        nthWeekdayOfMonth(year, 11, 4, 4), // Thanksgiving
    ];
    return [...fixed.map(observedFederalHoliday), ...floating].sort();
}
function observedFederalHoliday(date) {
    const weekday = dayOfWeek(date);
    if (weekday === 6)
        return addDays(date, -1);
    if (weekday === 0)
        return addDays(date, 1);
    return date;
}
const holidayCache = new Map();
export function isFederalHoliday(date) {
    const year = toParts(date).year;
    let set = holidayCache.get(year);
    if (!set) {
        set = new Set(federalHolidays(year));
        holidayCache.set(year, set);
    }
    return set.has(date);
}
export function isBusinessDay(date) {
    const weekday = dayOfWeek(date);
    return weekday !== 0 && weekday !== 6 && !isFederalHoliday(date);
}
/**
 * Moves a date off a weekend or holiday according to the obligation's rule.
 *
 * Steps one day at a time rather than jumping, because a Friday holiday
 * preceded by a weekend needs three steps and a fixed offset would land back on
 * a closed day. Bounded at ten iterations, which no run of closed days
 * approaches, so a bug here cannot become an infinite loop in a forecast.
 */
export function adjustForBusinessDay(date, adjustment) {
    if (adjustment === 'none')
        return date;
    const step = adjustment === 'previous_business_day' ? -1 : 1;
    let candidate = date;
    for (let guard = 0; guard < 10; guard += 1) {
        if (isBusinessDay(candidate))
            return candidate;
        candidate = addDays(candidate, step);
    }
    return candidate;
}
/**
 * Every occurrence of an obligation between two dates, inclusive.
 *
 * Generated from `startsOn` forwards rather than from the window backwards, and
 * that is what makes a biweekly schedule correct: "every fourteen days from the
 * 3rd of January" has a phase, and a window that computed its own starting point
 * would drift by up to thirteen days each time the window moved.
 *
 * The iteration is bounded by a generous cap on occurrences as well as by the
 * window, so a corrupt `startsOn` far in the past cannot spin.
 */
export function occurrencesBetween(obligation, from, to) {
    const out = [];
    /**
     * Generated slightly past the window, then filtered on the adjusted date.
     *
     * The overshoot is load-bearing rather than defensive. A payroll scheduled for
     * Saturday 1 August and paid on Friday 31 July belongs to July's window — but
     * generating only up to 31 July would never produce the 1 August date to
     * adjust, and the payment would vanish from both months. Ten days is the same
     * bound `adjustForBusinessDay` steps within, so nothing an adjustment can
     * reach is missed.
     *
     * `endsOn` is *not* overshot. That is a fact about the obligation rather than
     * about the window: an obligation that ended on the 31st has no occurrence in
     * August to pull back.
     */
    const generateTo = addDays(to, 10);
    const limit = obligation.endsOn && obligation.endsOn < generateTo ? obligation.endsOn : generateTo;
    for (const scheduledDate of scheduleDates(obligation, limit)) {
        if (scheduledDate > limit)
            break;
        const date = adjustForBusinessDay(scheduledDate, obligation.dateAdjustment);
        /**
         * Filtered on the **adjusted** date, because that is when the money
         * actually leaves. A payroll nominally due on the 1st and paid on the last
         * business day of the previous month belongs to the earlier window, and
         * filtering on the nominal date would drop it from both.
         */
        if (date < from || date > to)
            continue;
        out.push({ date, scheduledDate, amountMinor: obligation.amountMinor });
    }
    return out;
}
/** The raw schedule, before weekend and holiday adjustment. */
function* scheduleDates(obligation, limit) {
    const start = obligation.startsOn;
    if (obligation.frequency === 'one_off') {
        yield start;
        return;
    }
    /** Ten years of weekly occurrences; nothing legitimate reaches it. */
    const MAX_OCCURRENCES = 520;
    if (obligation.frequency === 'semi_monthly') {
        const days = obligation.semiMonthlyDays ?? [1, 15];
        const ordered = [...days].sort((a, b) => a - b);
        const { year, month } = toParts(start);
        let cursor = { year, month };
        for (let i = 0; i < MAX_OCCURRENCES; i += 1) {
            for (const day of ordered) {
                const date = dayOfMonthClamped(cursor.year, cursor.month, day);
                if (date < start)
                    continue;
                if (date > limit)
                    return;
                yield date;
            }
            cursor =
                cursor.month === 12
                    ? { year: cursor.year + 1, month: 1 }
                    : { year: cursor.year, month: cursor.month + 1 };
        }
        return;
    }
    const stepDays = obligation.frequency === 'weekly' ? 7 : obligation.frequency === 'biweekly' ? 14 : 0;
    const stepMonths = obligation.frequency === 'monthly'
        ? 1
        : obligation.frequency === 'quarterly'
            ? 3
            : obligation.frequency === 'annual'
                ? 12
                : 0;
    /**
     * Monthly and longer cadences step from the *original* start date rather than
     * from the previously produced one. Stepping from the produced date would let
     * a clamp compound — the 31st clamped to the 28th in February would then
     * produce the 28th in March and every month after, and the obligation would
     * silently move by three days for good.
     */
    for (let i = 0; i < MAX_OCCURRENCES; i += 1) {
        const date = stepDays > 0 ? addDays(start, stepDays * i) : addMonths(start, stepMonths * i);
        if (date > limit)
            return;
        yield date;
    }
}
/* ------------------------------------------------------------------------ *
 * The forecast (KAN-90)
 * ------------------------------------------------------------------------ */
/**
 * The horizons the forecast is offered over.
 *
 * KAN-49 names 30/60/90 and this is that list, as a closed set, because the
 * horizon appears in a query string and an unbounded one would let a request
 * ask for a decade of daily rows.
 */
export const CASH_HORIZONS = [30, 60, 90];
/**
 * How much of a receivable is assumed to arrive within the horizon, by how
 * overdue it already is, in basis points.
 *
 * These are **starting** figures, and the screen says so. KAN-90 asks for
 * weighting by "historical collection timing per aging bucket", which requires
 * a history of settled invoices this platform will only have once Clio has been
 * connected for a few cycles. Until then the shape is right and the numbers are
 * a stated assumption rather than a measurement — which is a different and much
 * more honest claim than a naive "everything pays on its due date".
 *
 * The direction is the one every collections professional would recognise: the
 * older a receivable, the less of it arrives, and past ninety days most of what
 * is going to be paid already has been.
 */
export const DEFAULT_COLLECTION_WEIGHTS_BPS = {
    not_yet_due: { conservative: 7_000, expected: 8_500, optimistic: 9_500 },
    days_0_30: { conservative: 5_000, expected: 7_000, optimistic: 8_500 },
    days_31_60: { conservative: 3_000, expected: 5_000, optimistic: 7_000 },
    days_61_90: { conservative: 1_500, expected: 3_000, optimistic: 5_000 },
    days_90_plus: { conservative: 500, expected: 1_500, optimistic: 3_000 },
};
/**
 * How much of a promise to pay is assumed to arrive, in basis points.
 *
 * Starting figures, stated as assumptions on the screen, like the collection
 * weights above. A promise is better evidence than an aging bucket — somebody
 * spoke to the client and the client named a date — so a promise not yet due
 * is weighted above a receivable not yet due. One whose date has passed with
 * no payment cleared is weighted well below it: a missed promise is the
 * strongest signal collections has that the money is not coming soon.
 */
export const PROMISE_WEIGHTS_BPS = {
    not_yet_due: { conservative: 6_000, expected: 9_000, optimistic: 10_000 },
    past_due: { conservative: 1_000, expected: 3_000, optimistic: 5_000 },
};
/**
 * What a new client is assumed to deposit, once, in the month they sign.
 *
 * Once rather than monthly: after the first month a client's payments are
 * invoices, and the forecast already counts those. Counting $1,500 a month on
 * top would count the same client twice.
 */
export const NEW_CLIENT_FIRST_MONTH_MINOR = 150_000;
/** Complete months the new-client run rate averages over. */
export const NEW_CLIENT_RUN_RATE_MONTHS = 3;
/**
 * The new-client projection is in the expected and optimistic bands and not
 * the conservative one. The conservative band counts only money tied to a
 * record — an invoice, a promise, a transfer — and a run rate is not one.
 */
export const NEW_CLIENT_WEIGHTS_BPS = {
    conservative: 0,
    expected: 10_000,
    optimistic: 10_000,
};
/**
 * The balance below which a projected day is called a shortfall.
 *
 * Zero, and zero is the only defensible default — the same reasoning the
 * `cash_shortage` alert gives for its own threshold. "Below nothing" is a
 * statement about the sign rather than about a buffer somebody chose, and
 * Finance can raise it in the query string to forecast against a floor they do
 * want to hold.
 */
export const DEFAULT_CASH_FLOOR_MINOR = 0;
/* ------------------------------------------------------------------------ *
 * Payment prioritisation (KAN-90)
 * ------------------------------------------------------------------------ */
/**
 * How urgent Finance considers a bill, independent of its date.
 *
 * Three levels rather than a numeric score, and the reason is the one the AR
 * dashboard gives for refusing to compute a weighted collection-priority
 * number: a figure assembled from invented coefficients reads as authoritative
 * and cannot be checked by the person acting on it. "Critical" is a judgement
 * somebody made and can defend; 87.3 is not.
 */
export const PAYMENT_CRITICALITIES = ['critical', 'normal', 'deferrable'];
export const PAYMENT_CRITICALITY_LABELS = {
    critical: 'Critical',
    normal: 'Normal',
    deferrable: 'Can be deferred',
};
/**
 * Maps the advisory integer already on `vendor_payments.priority` onto the three
 * levels. The column predates this module (it was added with the table for
 * KAN-49) and is left as it is rather than migrated, so nothing that already
 * writes it breaks; the mapping is stated once, here.
 */
export function criticalityFromPriority(priority) {
    if (priority === null)
        return 'normal';
    if (priority <= 1)
        return 'critical';
    if (priority >= 3)
        return 'deferrable';
    return 'normal';
}
//# sourceMappingURL=cashflow.js.map